<script lang="ts">
  import { uniqueFilter } from 'shared/src/utils/uniqueFilter';
  import H from '../../../../website/src/lib/components/H.svelte';
  import Radio from '../../../../website/src/lib/components/Radio.svelte';
  import Checkbox from '../../../../website/src/lib/components/Checkbox.svelte';
  import Button from '../../../../website/src/lib/components/Button.svelte';
  import type { NameTheRhythmQuestion } from './nameTheRhythm';
  import {
    baseDurations,
    type BaseDuration,
    rhythmNamesDictionary,
  } from 'shared/definitions/musicTheory/rhythms';
  import { getRhythmHTML } from '../../uTheory.Notation/rhythmSpan';
  import Rhythm from '../../uTheory.Notation/rhythm';
  import { createEventDispatcher, onMount } from 'svelte';
  import RhythmSpan from '../../uTheory.Notation/RhythmSpan.svelte';
  import type { AnswerAndData } from '../interactiveQuestionPrototype';

  export let question: NameTheRhythmQuestion | undefined = undefined;

  export let rhythmicValues: NameTheRhythmQuestion[];

  let dotted = false;
  let duration: BaseDuration | null;
  let noteOrRest: 'Note' | 'Rest' | null = null;

  function reset() {
    dotted = false;
    duration = null;
    noteOrRest = null;
  }

  $: if (question) reset();

  $: durations = rhythmicValues
    .map(
      (rhythm): BaseDuration =>
        rhythm
          .split('')
          .filter((c) => c !== '.' && c !== 'r')
          .join('') as BaseDuration,
    )
    .filter(uniqueFilter)
    .sort((a, b) => baseDurations.indexOf(a) - baseDurations.indexOf(b))
    .map((duration) => ({
      duration,
      name: rhythmNamesDictionary[duration],
    }));

  $: hasDots = rhythmicValues
    .join('')
    .split('')
    .find((c) => c === '.');

  $: answerText =
    duration && noteOrRest
      ? (dotted ? 'Dotted ' : '') +
        (duration ? rhythmNamesDictionary[duration] : '') +
        ' ' +
        noteOrRest
      : 'Unanswered';

  $: rhythm = question
    ? new Rhythm(
        question
          .split('')
          .filter((c) => c !== 'r')
          .join(''),
      )
    : null;
  $: correctAnswerText = rhythm
    ? (rhythm.dots ? 'Dotted ' : '') +
      rhythmNamesDictionary[rhythm.baseDuration] +
      ' ' +
      (question?.includes('r') ? 'Rest' : 'Note')
    : '';

  $: rhythmString = `4/2: ${question || 'q'}`;

  let answerAndData: AnswerAndData;
  $: answerAndData = {
    question: question || '',
    questionType: 'NameTheRhythm',
    questionText: `What rhythmic value is ${getRhythmHTML({
      rhythmString,
      color: '#000',
    })}`,
    studentAnswerText: answerText,
    correctAnswerText,
    rightAnswers: answerText === correctAnswerText ? 1 : 0,
    wrongAnswers: answerText !== correctAnswerText ? 1 : 0,
    skills: [],
  };

  $: console.log({ noteOrRest, duration, question });

  const dispatch = createEventDispatcher<{ ANSWER_ATTEMPT: AnswerAndData; mounted: null }>();

  onMount(() => {
    console.log('dispatching mounted...');
    dispatch('mounted');
  });

  export const getAnswerAndData = () => answerAndData;
</script>

<H class="text-center" level={2}>What rhythmic value is this?</H>
<div class="text-4xl text-center">
  <RhythmSpan {rhythmString} />
</div>
<div class="max-w-lg mx-auto my-4">
  {#if hasDots}
    <div class="border-b p-4">
      <Checkbox class="w-fit mx-auto text-center" bind:checked={dotted}>Dotted</Checkbox>
    </div>
  {/if}
  <div class="border-b p-4 flex flex-wrap justify-around mx-auto">
    {#each durations as { duration: durationValue, name }}
      <div class="whitespace-nowrap mx-2">
        <Radio class="inline-block" bind:group={duration} value={durationValue}>{name}</Radio>
      </div>
    {/each}
  </div>
  <div class="border-b p-4 whitespace-nowrap">
    <div class="mx-auto w-fit">
      <Radio class="inline-block" bind:group={noteOrRest} value="Note">Note</Radio><br />
      <Radio class="inline-block" bind:group={noteOrRest} value="Rest">Rest</Radio>
    </div>
  </div>
</div>
<div class="w-fit mx-auto">
  <Button
    on:click={() => dispatch('ANSWER_ATTEMPT', answerAndData)}
    disabled={!noteOrRest || !duration || !question}>Submit</Button
  >
</div>
